/**
 * 🧭 상단 헤더 한 줄 (v2.319.0 · Salesforce 전역 헤더 · 기능·메뉴 검토 I3 = 지난 A1·A3)
 *
 *   왼쪽  🔍 전역 검색 — 입력하면 영업건·거래처·담당자·유지보수·딜·견적을 묶어 보여 준다(그룹마다 5개 · Enter = 첫 결과 · Esc = 닫기)
 *   오른쪽 🕘 최근 본 항목(20) · ★ 즐겨찾기(이 화면 넣기/빼기 · 목록) · 🔔 알림(내 결재 대기 + 할 일 + 납품 임박 + 미발행 + 딜·유지보수 만료) ·
 *          [💬 사용 문의] [📮 개발요청](PC 는 여기 · 폰은 종전대로 오른쪽 아래 떠 있는 단추 — DevRequestFab CornerActions)
 *   PC(md 이상)에만 보인다. 본문 위에 붙어(sticky) 화면이 스크롤돼도 남는다.
 *   ⌨ Ctrl+F · Ctrl+K = 이 검색창으로 (v2.322.0 · 개발요청 #33 · 규칙은 lib/search-hotkey.ts 한 곳 — 검색창에서 Ctrl+F 한 번 더 = 브라우저 찾기 · 작성 창이 열려 있으면 브라우저 그대로)
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { dialogDepth, useDialogLayer } from '../lib/dialog-stack';
import { searchHotkeyAction } from '../lib/search-hotkey';
import * as approvalApi from '../lib/approval-api';
import { pageTitle } from '../lib/menu';
import { addFavorite, bellCounts, listFavorites, listRecent, navSearch, removeFavorite, RECENT_KIND_LABEL, type SearchGroup } from '../lib/nav-api';
import { MenuIcon } from './ui/MenuIcon';
import { HelpBotFab } from './HelpBotFab';
import { DevRequestFab } from './DevRequestFab';

type Open = 'search' | 'recent' | 'fav' | 'bell' | null;

export function TopBar() {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState<Open>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Esc 는 창 겹침 규칙(lib/dialog-stack · 맨 위 층만 닫힌다)으로 · 바깥 누르면 닫기
  useDialogLayer(open !== null, () => setOpen(null));
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(null); };
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('mousedown', onDown); };
  }, [open]);
  // 화면이 바뀌면 닫기
  useEffect(() => { setOpen(null); }, [pathname, search]);

  /* ── 검색 ── */
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const openRef = useRef<Open>(null);
  openRef.current = open;
  // ⌨ Ctrl+F · Ctrl+K → 이 검색창 (v2.322.0 · #33) — 무엇을 막고 무엇을 브라우저에 넘기나는 searchHotkeyAction 이 정한다
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.defaultPrevented || e.isComposing) return;
      const el = inputRef.current;
      if (!el) return;
      const act = searchHotkeyAction(e, {
        inSearch: document.activeElement === el,
        // 검색 목록·최근·즐겨찾기·알림이 열려 있으면 그것도 창 한 층(위 useDialogLayer)이다 — 그 층은 빼고 센다
        otherDialogs: dialogDepth() - (openRef.current !== null ? 1 : 0),
        searchVisible: el.offsetParent !== null,
      });
      if (act !== 'focus') return;
      e.preventDefault();
      el.focus();
      el.select();
      setOpen('search');
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, []);
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setDebounced(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const searchQ = useQuery({ queryKey: ['nav', 'search', debounced], queryFn: () => navSearch(debounced), enabled: debounced.length >= 1 && open === 'search', staleTime: 30_000 });
  const recentQ = useQuery({ queryKey: ['nav', 'recent'], queryFn: listRecent, enabled: !!user && (open === 'recent' || open === 'search'), staleTime: 30_000 });
  const go = (path: string): void => { setOpen(null); setQ(''); navigate(path); };
  const firstResult = (groups: SearchGroup[] | undefined): string | null => groups?.find((g) => g.items.length)?.items[0]?.path ?? null;

  /* ── 즐겨찾기 ── */
  const here = pathname + search;
  const hereLabel = pageTitle(pathname, search).replace(/ · SM 포털$/, '');
  const favQ = useQuery({ queryKey: ['nav', 'favorites'], queryFn: listFavorites, enabled: !!user, staleTime: 60_000 });
  const isFav = (favQ.data ?? []).some((f) => f.path === here);
  const favMut = useMutation({
    mutationFn: async () => { if (isFav) await removeFavorite(here); else await addFavorite(here, hereLabel); },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['nav', 'favorites'] }); },
    onError: (e: Error) => alert(`즐겨찾기 저장 실패: ${e.message}`),
  });
  const favRemove = useMutation({ mutationFn: (path: string) => removeFavorite(path), onSuccess: () => { void qc.invalidateQueries({ queryKey: ['nav', 'favorites'] }); } });

  /* ── 알림 ── */
  const bellQ = useQuery({ queryKey: ['nav', 'bell'], queryFn: bellCounts, enabled: !!user, staleTime: 60_000, refetchInterval: 5 * 60_000 });
  const pendingQ = useQuery({ queryKey: ['approvals', 'pendingCount'], queryFn: approvalApi.getPendingCounts, enabled: !!user, staleTime: 60_000 });
  const pending = pendingQ.data?.count ?? 0;
  const bellItems = [
    ...(pending > 0 || user?.role !== 'viewer' ? [{ key: 'approvals', label: '내 결재 대기', count: pending, path: '/approvals', tone: 'err' as const }] : []),
    ...(bellQ.data?.items ?? []),
  ];
  const bellTotal = bellItems.reduce((s, i) => s + i.count, 0);

  if (!user) return null;
  const toneCls: Record<string, string> = { err: 'text-red-600', warn: 'text-amber-700', info: 'text-blue-700' };

  return (
    <div ref={rootRef} className="hidden md:flex sticky top-0 z-30 h-11 items-center gap-2 border-b border-slate-200 bg-white px-4 print:hidden">
      {/* 검색 */}
      <div className="relative flex-1 max-w-xl">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true"><MenuIcon name="search" size={16} /></span>
        <input
          ref={inputRef}
          id="topbar-search"
          type="search"
          title="검색 (Ctrl+F · Ctrl+K) — 검색창에서 Ctrl+F 를 한 번 더 누르면 브라우저 찾기"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen('search'); }}
          onFocus={() => setOpen('search')}
          onKeyDown={(e) => { if (e.key === 'Enter') { const p = firstResult(searchQ.data); if (p) go(p); } }}
          placeholder="검색 — 매출번호 · 엔드유저 · 거래처 · 담당자 · 프로젝트 · 계약 · 견적번호"
          className="ui-input h-8 w-full pl-8 pr-16"
          autoComplete="off"
        />
        {q === '' && (
          <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border border-slate-200 bg-slate-50 px-1.5 font-sans text-[11px] leading-4 text-slate-400" aria-hidden="true">Ctrl+F</kbd>
        )}
        {open === 'search' && (
          <div className="absolute left-0 right-0 top-9 max-h-[70vh] overflow-y-auto rounded border border-slate-200 bg-white shadow-md">
            {debounced.length === 0 ? (
              <div className="p-2">
                <div className="px-2 py-1 text-xs text-slate-500">최근 본 항목</div>
                {(recentQ.data ?? []).length === 0 && <div className="px-2 py-2 text-sm text-slate-400">아직 없음 — 영업건·거래처를 열면 여기에 쌓입니다</div>}
                {(recentQ.data ?? []).slice(0, 8).map((r) => (
                  <button key={`${r.kind}-${r.refId}`} type="button" onClick={() => go(r.path)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                    <span className="w-14 shrink-0 text-xs text-slate-500">{RECENT_KIND_LABEL[r.kind]}</span><span className="min-w-0 truncate">{r.label}</span>
                  </button>
                ))}
              </div>
            ) : searchQ.isLoading ? (
              <div className="p-3 text-sm text-slate-400">찾는 중…</div>
            ) : (searchQ.data ?? []).length === 0 ? (
              <div className="p-3 text-sm text-slate-400">「{debounced}」 결과 없음</div>
            ) : (
              <div className="p-2">
                {(searchQ.data ?? []).map((g) => (
                  <div key={g.kind} className="mb-1">
                    <div className="px-2 py-1 text-xs font-semibold text-slate-500">{g.label}</div>
                    {g.items.map((it) => (
                      <button key={it.id} type="button" onClick={() => go(it.path)} className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left hover:bg-slate-50">
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm">{it.title}</span>
                          <span className="block truncate text-xs text-slate-500">{it.sub}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
                <div className="px-2 pt-1 text-[11px] text-slate-400">Enter = 첫 결과 열기 · 그룹마다 5개까지</div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-1">
        {/* 최근 */}
        <div className="relative">
          <button type="button" onClick={() => setOpen(open === 'recent' ? null : 'recent')} className="btn btn-ghost btn-sm" title="최근 본 항목" aria-expanded={open === 'recent'}>🕘 최근</button>
          {open === 'recent' && (
            <div className="absolute right-0 top-8 w-80 max-h-[60vh] overflow-y-auto rounded border border-slate-200 bg-white p-2 shadow-md">
              <div className="px-2 py-1 text-xs text-slate-500">최근 본 항목 {(recentQ.data ?? []).length}</div>
              {(recentQ.data ?? []).length === 0 && <div className="px-2 py-2 text-sm text-slate-400">아직 없음 — 영업건·거래처를 열면 여기에 쌓입니다</div>}
              {(recentQ.data ?? []).map((r) => (
                <button key={`${r.kind}-${r.refId}`} type="button" onClick={() => go(r.path)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                  <span className="w-14 shrink-0 text-xs text-slate-500">{RECENT_KIND_LABEL[r.kind]}</span><span className="min-w-0 truncate">{r.label}</span>
                  <span className="ml-auto shrink-0 text-[11px] text-slate-400">{r.viewedAt.slice(5, 10).replace('-', '/')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* 즐겨찾기 */}
        <div className="relative flex items-center">
          <button type="button" onClick={() => favMut.mutate()} disabled={favMut.isPending} className={`btn btn-ghost btn-sm ${isFav ? 'text-amber-500' : ''}`} title={isFav ? `즐겨찾기에서 빼기 — ${hereLabel}` : `이 화면을 즐겨찾기에 넣기 — ${hereLabel}`} aria-pressed={isFav}>{isFav ? '★' : '☆'}</button>
          <button type="button" onClick={() => setOpen(open === 'fav' ? null : 'fav')} className="btn btn-ghost btn-sm -ml-1 px-1" title="즐겨찾기 목록" aria-expanded={open === 'fav'}>▼</button>
          {open === 'fav' && (
            <div className="absolute right-0 top-8 w-80 max-h-[60vh] overflow-y-auto rounded border border-slate-200 bg-white p-2 shadow-md">
              <div className="px-2 py-1 text-xs text-slate-500">즐겨찾기 {(favQ.data ?? []).length}</div>
              {(favQ.data ?? []).length === 0 && <div className="px-2 py-2 text-sm text-slate-400">아직 없음 — ☆ 를 누르면 지금 화면(탭·필터 포함)이 저장됩니다</div>}
              {(favQ.data ?? []).map((f) => (
                <div key={f.id} className="flex items-center gap-1 rounded px-1 hover:bg-slate-50">
                  <Link to={f.path} onClick={() => setOpen(null)} className="min-w-0 flex-1 truncate px-1 py-1.5 text-sm">{f.label}</Link>
                  <button type="button" onClick={() => favRemove.mutate(f.path)} className="btn btn-ghost btn-sm px-1 text-slate-400 hover:text-red-600" title="빼기">×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* 알림 */}
        <div className="relative">
          <button type="button" onClick={() => setOpen(open === 'bell' ? null : 'bell')} className="btn btn-ghost btn-sm relative" title="알림" aria-expanded={open === 'bell'}>
            🔔
            {bellTotal > 0 && <span className="absolute -right-0.5 -top-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[11px] font-bold text-white">{bellTotal > 99 ? '99+' : bellTotal}</span>}
          </button>
          {open === 'bell' && (
            <div className="absolute right-0 top-8 w-80 rounded border border-slate-200 bg-white p-2 shadow-md">
              <div className="px-2 py-1 text-xs text-slate-500">지금 봐야 할 것 {bellQ.data?.today ? `· ${bellQ.data.today}` : ''}</div>
              {bellItems.map((i) => (
                <button key={i.key} type="button" onClick={() => go(i.path)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                  <span className="min-w-0 flex-1 truncate">{i.label}</span>
                  <b className={i.count > 0 ? toneCls[i.tone] : 'text-slate-400'}>{i.count}</b>
                </button>
              ))}
              <div className="flex items-center gap-2 px-2 pt-1 text-[11px] text-slate-400">
                <span className="min-w-0 flex-1">5분마다 새로 셉니다{(bellQ.data?.hidden.length ?? 0) > 0 ? ` · 숨긴 항목 ${bellQ.data!.hidden.length}개` : ''}</span>
                {/* 사람마다 띄울 항목 고르기 (v2.323.0 · 개발요청 #34) — 마이페이지 「🔔 알림 종」 칸 */}
                <button type="button" onClick={() => go('/mypage#bell')} className="shrink-0 text-blue-700 hover:underline">표시 항목 고르기 →</button>
              </div>
            </div>
          )}
        </div>
        <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
        <HelpBotFab />
        <DevRequestFab />
      </div>
    </div>
  );
}
